import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import Radio from '@mui/material/Radio';
import { useTheme } from '@mui/material/styles';
import { Facet, FacetValue, loadSearchActions, loadSearchAnalyticsActions } from '@coveo/headless';
import ButtonDiscreet from 'components/shared/Button/ButtonDiscreet';
import ButtonPrimary from 'components/shared/Button/PrimaryButton';
import UiText from 'components/shared/Typography/UiText';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import {
  LocationSearchFacetKey,
  LocationSearchFilterBarProps,
  LocationSearchFacetControllers,
  SearchLocationFilterField,
} from '../LocationSearch.types';
import {
  buildStagedSelectionFromFacet,
  DistanceFilterMenuOption,
  formatDistancePillValue,
  getLocationSearchDistanceOptions,
  getFacetController,
  getSelectedFacetValues,
  isFilterVisible,
  LOCATION_SEARCH_FILTER_KEYS,
  LocationSearchFilterKey,
  resolveFilterKey,
  resolveFilterLabel,
  sortFiltersWithDistanceFirst,
  toSitecoreItemList,
} from '../locationSearchFilterConfig';
import { formatStatusFacetLabel } from '../formatStatusFacetLabel';
import {
  StClearFilterButtonWrap,
  StFilterBar,
  StFilterBarInner,
  StFilterCheckboxChecked,
  StFilterCheckboxUnchecked,
  StFilterCountBadge,
  StFilterMenuActions,
  StFilterMenuDivider,
  StFilterMenuEmpty,
  StFilterMenuOption,
  StFilterMenuOptions,
  StFilterMenuPaper,
  StFilterMeta,
  StFilterPill,
  StFilterPills,
} from './LocationSearchFilterBar.styles';

const resolveSitesFoundText = (template: string | undefined, count: number): string => {
  return (template ?? '{count} sites found').replace('{count}', String(count)).trim();
};

const FILTER_MENU_ACTION_STYLE = {
  minHeight: '32px',
  height: '32px',
  fontSize: '13px',
  padding: '0 16px',
};

const MENU_OPTION_SELECTOR = '[data-filter-menu-option]:not([tabindex="-1"])';
const MENU_FOCUSABLE_SELECTOR = `${MENU_OPTION_SELECTOR}, button:not([disabled])`;

export const executeFacetSearch = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const { executeSearch } = loadSearchActions(sitesHeadlessEngine);
  const { logInterfaceChange } = loadSearchAnalyticsActions(sitesHeadlessEngine);
  sitesHeadlessEngine.dispatch(executeSearch(logInterfaceChange()));
};

const applyStagedFacetSelection = (facet: Facet, staged: Record<string, boolean>): void => {
  facet.state.values.forEach((value) => {
    const shouldSelect = Boolean(staged[value.value]);
    const isSelected = facet.isValueSelected(value);
    if (shouldSelect !== isSelected) {
      facet.toggleSelect(value);
    }
  });
};

const focusFirstMenuItem = (container: HTMLElement | null): void => {
  if (!container) {
    return;
  }
  const first = container.querySelector<HTMLElement>(MENU_OPTION_SELECTOR);
  first?.focus();
};

const MAX_VISIBLE_FILTER_OPTIONS = 5;
const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 8] } }];
const GROW_TIMEOUT = { enter: 180, exit: 140 };

interface DistanceFilterMenuOptionItemProps {
  option: DistanceFilterMenuOption;
  isSelected: boolean;
  optionId: string;
  onSelect: (option: DistanceFilterMenuOption) => void;
}

function DistanceFilterMenuOptionItem({
  option,
  isSelected,
  optionId,
  onSelect,
}: DistanceFilterMenuOptionItemProps): JSX.Element {
  const handleChange = useCallback(() => {
    onSelect(option);
  }, [onSelect, option]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(option);
      }
    },
    [onSelect, option]
  );

  return (
    <StFilterMenuOption
      id={optionId}
      $selected={isSelected}
      value={option.label}
      tabIndex={0}
      data-filter-menu-option
      control={
        <Radio
          size="small"
          checked={isSelected}
          tabIndex={-1}
          onChange={handleChange}
          inputProps={{ 'aria-label': option.label }}
        />
      }
      label={option.label}
      onKeyDown={handleKeyDown}
    />
  );
}

interface DistanceFilterMenuProps {
  options: DistanceFilterMenuOption[];
  selectedLabel: string | null;
  menuId: string;
  onSelect: (option: DistanceFilterMenuOption) => void;
}

function DistanceFilterMenu({
  options,
  selectedLabel,
  menuId,
  onSelect,
}: DistanceFilterMenuProps): JSX.Element {
  const scrollable = options.length > MAX_VISIBLE_FILTER_OPTIONS;

  const handleRadiogroupKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }

      event.preventDefault();

      const currentIndex = options.findIndex((option) => option.label === selectedLabel);
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex =
        event.key === 'ArrowDown'
          ? (startIndex + 1) % options.length
          : startIndex <= 0
            ? options.length - 1
            : startIndex - 1;
      const nextOption = options[nextIndex];

      onSelect(nextOption);
      document.getElementById(`${menuId}-option-${nextOption.id}`)?.focus();
    },
    [menuId, onSelect, options, selectedLabel]
  );

  return (
    <StFilterMenuOptions $scrollable={scrollable}>
      <Box
        id={menuId}
        role="radiogroup"
        aria-label="Distance"
        onKeyDown={handleRadiogroupKeyDown}
        sx={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}
      >
        {options.map((option) => (
          <DistanceFilterMenuOptionItem
            key={option.id}
            option={option}
            optionId={`${menuId}-option-${option.id}`}
            isSelected={selectedLabel === option.label}
            onSelect={onSelect}
          />
        ))}
      </Box>
    </StFilterMenuOptions>
  );
}

interface FacetFilterMenuOptionItemProps {
  value: FacetValue;
  displayLabel: string;
  staged: Record<string, boolean>;
  isLoading: boolean;
  onToggle: (value: FacetValue) => void;
}

function FacetFilterMenuOptionItem({
  value,
  displayLabel,
  staged,
  isLoading,
  onToggle,
}: FacetFilterMenuOptionItemProps): JSX.Element {
  const checked = Boolean(staged[value.value]);

  const handleChange = useCallback(() => {
    onToggle(value);
  }, [onToggle, value]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isLoading) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle(value);
      }
    },
    [isLoading, onToggle, value]
  );

  return (
    <StFilterMenuOption
      $selected={checked}
      tabIndex={isLoading ? -1 : 0}
      data-filter-menu-option
      control={
        <Checkbox
          size="small"
          checked={checked}
          tabIndex={-1}
          onChange={handleChange}
          disabled={isLoading}
          icon={<StFilterCheckboxUnchecked />}
          checkedIcon={
            <StFilterCheckboxChecked
              data-testid="filter-checkbox-checked"
              id="filter-checkbox-checked"
            />
          }
          inputProps={{ 'aria-label': displayLabel }}
        />
      }
      label={displayLabel}
      onKeyDown={handleKeyDown}
    />
  );
}

interface FacetFilterMenuProps {
  facetKey: LocationSearchFacetKey;
  facet: Facet;
  menuId: string;
  clearLabel?: string;
  applyLabel?: string;
  staged: Record<string, boolean>;
  onStagedChange: (next: Record<string, boolean>) => void;
  onClear: () => void;
  onApply: () => void;
}

function FacetFilterMenu({
  facetKey,
  facet,
  menuId,
  clearLabel,
  applyLabel,
  staged,
  onStagedChange,
  onClear,
  onApply,
}: FacetFilterMenuProps): JSX.Element {
  const theme = useTheme();
  const facetState = facet.state;
  const scrollable = facetState.values.length > MAX_VISIBLE_FILTER_OPTIONS;

  const handleToggle = useCallback(
    (value: FacetValue) => {
      onStagedChange({
        ...staged,
        [value.value]: !staged[value.value],
      });
    },
    [onStagedChange, staged]
  );

  const hasStagedSelection = useMemo(() => Object.values(staged).some(Boolean), [staged]);

  const hasAppliedSelection = useMemo(
    () => facetState.values.some((value) => facet.isValueSelected(value)),
    [facet, facetState.values]
  );

  const isDirty = useMemo(
    () =>
      facetState.values.some(
        (value) => Boolean(staged[value.value]) !== facet.isValueSelected(value)
      ),
    [facet, facetState.values, staged]
  );

  const canClear = hasStagedSelection || hasAppliedSelection;
  const canApply = hasStagedSelection || isDirty;

  return (
    <Box
      id={menuId}
      role="group"
      aria-label={facetKey}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        height: scrollable ? '100%' : 'auto',
        minHeight: 0,
        gap: '2px',
      }}
    >
      <StFilterMenuOptions
        id="filter-menu-options"
        data-testid="filter-menu-options"
        data-scrollable={scrollable ? 'true' : 'false'}
        $scrollable={scrollable}
      >
        {facetState.values.length === 0 ? (
          <StFilterMenuEmpty>
            <UiText
              size="body2"
              styles={{ fontSize: '14px', color: theme.palette.neutral11?.main }}
            >
              No options available
            </UiText>
          </StFilterMenuEmpty>
        ) : (
          facetState.values.map((value) => (
            <FacetFilterMenuOptionItem
              key={value.value}
              value={value}
              displayLabel={
                facetKey === LOCATION_SEARCH_FILTER_KEYS.STATUS
                  ? formatStatusFacetLabel(value.value)
                  : value.value
              }
              staged={staged}
              isLoading={facetState.isLoading}
              onToggle={handleToggle}
            />
          ))
        )}
      </StFilterMenuOptions>

      <StFilterMenuDivider />

      <StFilterMenuActions>
        <StClearFilterButtonWrap>
          <ButtonDiscreet
            id="clear-filter-button"
            type="button"
            disabled={!canClear}
            aria-disabled={!canClear}
            style={FILTER_MENU_ACTION_STYLE}
            onClick={onClear}
          >
            {clearLabel}
          </ButtonDiscreet>
        </StClearFilterButtonWrap>
        <ButtonPrimary
          type="button"
          disabled={!canApply}
          aria-disabled={!canApply}
          style={FILTER_MENU_ACTION_STYLE}
          onClick={onApply}
        >
          {applyLabel}
        </ButtonPrimary>
      </StFilterMenuActions>
    </Box>
  );
}

interface FilterPillProps {
  item: SearchLocationFilterField;
  filterKey: LocationSearchFilterKey;
  label: string;
  isOpen: boolean;
  isDisabled: boolean;
  isActive: boolean;
  pillId: string;
  openMenuId: string | undefined;
  appliedCount: number;
  selectedDistanceLabel: string | null;
  isDistanceDisabled: boolean;
  onOpen: (key: LocationSearchFilterKey, element: HTMLElement) => void;
}

function FilterPill({
  item,
  filterKey,
  label,
  isOpen,
  isDisabled,
  isActive,
  pillId,
  openMenuId,
  appliedCount,
  selectedDistanceLabel,
  isDistanceDisabled,
  onOpen,
}: FilterPillProps): JSX.Element {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onOpen(filterKey, event.currentTarget);
    },
    [filterKey, onOpen]
  );

  const renderedLabel = useMemo(() => {
    if (
      filterKey === LOCATION_SEARCH_FILTER_KEYS.DISTANCE &&
      selectedDistanceLabel &&
      !isDistanceDisabled
    ) {
      return `${label}: ${formatDistancePillValue(selectedDistanceLabel)}`;
    }

    if (filterKey !== LOCATION_SEARCH_FILTER_KEYS.DISTANCE && appliedCount > 0) {
      return (
        <>
          {label}
          <StFilterCountBadge aria-label={`${appliedCount} selected`}>
            {appliedCount}
          </StFilterCountBadge>
        </>
      );
    }

    const labelField = item.fields?.distanceFilterLabel || item.fields?.filterLabel;
    const fieldValue = labelField?.value?.trim();
    return fieldValue ? <Text field={labelField} /> : label;
  }, [filterKey, label, appliedCount, selectedDistanceLabel, isDistanceDisabled, item.fields]);

  return (
    <StFilterPill
      id={pillId}
      type="button"
      $active={isActive}
      $open={isOpen}
      $disabled={isDisabled}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-expanded={isOpen}
      aria-haspopup="true"
      aria-controls={isOpen ? openMenuId : undefined}
      title={
        isDisabled ? 'Enter a location or use location services to filter by distance' : undefined
      }
      onClick={handleClick}
    >
      {renderedLabel}
      {isOpen ? <ExpandLessIcon aria-hidden /> : <ExpandMoreIcon aria-hidden />}
    </StFilterPill>
  );
}

interface FacetFilterMenuPanelProps {
  facetKey: LocationSearchFacetKey;
  facetControllers: LocationSearchFacetControllers | undefined;
  openFilterItem: SearchLocationFilterField;
  openMenuId: string;
  stagedSelections: Partial<Record<LocationSearchFacetKey, Record<string, boolean>>>;
  onStagedChange: (facetKey: LocationSearchFacetKey, next: Record<string, boolean>) => void;
  onClear: (facetKey: LocationSearchFacetKey) => void;
  onApply: (facetKey: LocationSearchFacetKey) => void;
}

function FacetFilterMenuPanel({
  facetKey,
  facetControllers,
  openFilterItem,
  openMenuId,
  stagedSelections,
  onStagedChange,
  onClear,
  onApply,
}: FacetFilterMenuPanelProps): JSX.Element {
  const theme = useTheme();
  const facet = getFacetController(facetControllers, facetKey);

  const handleStagedChange = useCallback(
    (next: Record<string, boolean>) => {
      onStagedChange(facetKey, next);
    },
    [facetKey, onStagedChange]
  );

  const handleClear = useCallback(() => {
    onClear(facetKey);
  }, [facetKey, onClear]);

  const handleApply = useCallback(() => {
    onApply(facetKey);
  }, [facetKey, onApply]);

  if (!facet) {
    return (
      <StFilterMenuEmpty>
        <UiText size="body2" styles={{ fontSize: '14px', color: theme.palette.neutral11?.main }}>
          No options available
        </UiText>
      </StFilterMenuEmpty>
    );
  }

  return (
    <FacetFilterMenu
      facetKey={facetKey}
      facet={facet}
      menuId={openMenuId}
      clearLabel={openFilterItem.fields?.clearLabel?.value || 'Clear'}
      applyLabel={openFilterItem.fields?.applyLabel?.value || 'Apply'}
      staged={stagedSelections[facetKey] || {}}
      onStagedChange={handleStagedChange}
      onClear={handleClear}
      onApply={handleApply}
    />
  );
}

function LocationSearchFilterBar({
  fields,
  sitesFoundCount = 0,
  hasLocationContext = false,
  selectedDistanceLabel = null,
  onDistanceChange,
  facetControllers,
  onClearAllFilters,
  hasCommunityFilter = false,
}: LocationSearchFilterBarProps): JSX.Element {
  const theme = useTheme();
  const baseId = useId();
  const filterFields = fields?.filterFields;
  const isDistanceDisabled = !hasLocationContext;

  const [openFilterKey, setOpenFilterKey] = useState<LocationSearchFilterKey | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [stagedSelections, setStagedSelections] = useState<
    Partial<Record<LocationSearchFacetKey, Record<string, boolean>>>
  >({});
  const [facetTick, setFacetTick] = useState(0);
  const menuPaperRef = useRef<HTMLDivElement | null>(null);

  const visibleFilters = useMemo(
    () =>
      sortFiltersWithDistanceFirst(
        toSitecoreItemList<SearchLocationFilterField>(filterFields).filter(isFilterVisible)
      ),
    [filterFields]
  );

  const distanceOptions = useMemo(() => getLocationSearchDistanceOptions(fields), [fields]);

  useEffect(() => {
    if (!facetControllers) {
      return;
    }

    const unsubscribers = (Object.keys(facetControllers) as LocationSearchFacetKey[])
      .map((key) => facetControllers[key])
      .filter((facet): facet is Facet => Boolean(facet))
      .map((facet) => facet.subscribe(() => setFacetTick((tick) => tick + 1)));

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [facetControllers]);

  const closeMenu = useCallback(() => {
    setOpenFilterKey(null);
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (isDistanceDisabled && openFilterKey === LOCATION_SEARCH_FILTER_KEYS.DISTANCE) {
      closeMenu();
    }
  }, [closeMenu, isDistanceDisabled, openFilterKey]);

  const openMenu = useCallback(
    (key: LocationSearchFilterKey, element: HTMLElement) => {
      if (key === LOCATION_SEARCH_FILTER_KEYS.DISTANCE && isDistanceDisabled) {
        return;
      }

      if (openFilterKey === key) {
        closeMenu();
        return;
      }

      if (key !== LOCATION_SEARCH_FILTER_KEYS.DISTANCE) {
        const facetKey = key as LocationSearchFacetKey;
        const facet = getFacetController(facetControllers, facetKey);
        setStagedSelections((prev) => ({
          ...prev,
          [facetKey]: buildStagedSelectionFromFacet(facet),
        }));
      }

      setOpenFilterKey(key);
      setAnchorEl(element);
    },
    [closeMenu, facetControllers, isDistanceDisabled, openFilterKey]
  );

  const prevOpenKeyRef = useRef<LocationSearchFilterKey | null>(null);
  useEffect(() => {
    const wasOpen = prevOpenKeyRef.current;
    prevOpenKeyRef.current = openFilterKey;

    if (wasOpen && !openFilterKey) {
      document.getElementById(`${baseId}-${wasOpen}-pill`)?.focus();
      return;
    }

    if (!openFilterKey) {
      return;
    }

    // Initial open is handled by Grow onEntered; re-focus when switching pills
    // while the menu stays mounted so keyboard focus does not drop to body.
    if (wasOpen && wasOpen !== openFilterKey) {
      const frameId = window.requestAnimationFrame(() => {
        focusFirstMenuItem(menuPaperRef.current);
      });
      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }
  }, [openFilterKey, baseId]);

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab' || !menuPaperRef.current) {
        return;
      }

      const focusable = Array.from(
        menuPaperRef.current.querySelectorAll<HTMLElement>(MENU_FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);

      if (focusable.length === 0) {
        return;
      }

      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      event.preventDefault();

      if (event.shiftKey) {
        const prevIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
        focusable[prevIndex].focus();
        return;
      }

      const nextIndex = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
      focusable[nextIndex].focus();
    },
    [closeMenu]
  );

  const handleDistanceSelect = useCallback(
    (option: DistanceFilterMenuOption) => {
      onDistanceChange?.(option.label, option.miles);
      closeMenu();
    },
    [closeMenu, onDistanceChange]
  );

  const handleFacetApply = useCallback(
    (facetKey: LocationSearchFacetKey) => {
      const facet = getFacetController(facetControllers, facetKey);
      const staged = stagedSelections[facetKey];
      if (!facet) {
        closeMenu();
        return;
      }

      applyStagedFacetSelection(facet, staged ?? {});
      executeFacetSearch();
      closeMenu();
    },
    [closeMenu, facetControllers, stagedSelections]
  );

  const handleFacetClear = useCallback(
    (facetKey: LocationSearchFacetKey) => {
      const facet = getFacetController(facetControllers, facetKey);
      if (!facet) {
        return;
      }

      const cleared = facet.state.values.reduce<Record<string, boolean>>((acc, value) => {
        acc[value.value] = false;
        return acc;
      }, {});
      setStagedSelections((prev) => ({ ...prev, [facetKey]: cleared }));
      facet.deselectAll();
      executeFacetSearch();
    },
    [facetControllers]
  );

  const handleClearAll = useCallback(() => {
    onClearAllFilters?.();
    closeMenu();
  }, [closeMenu, onClearAllFilters]);

  const handleStagedChange = useCallback(
    (facetKey: LocationSearchFacetKey, next: Record<string, boolean>) => {
      setStagedSelections((prev) => ({ ...prev, [facetKey]: next }));
    },
    []
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!openFilterKey) {
        return;
      }

      const activePill = document.getElementById(`${baseId}-${openFilterKey}-pill`);
      if (activePill && activePill.contains(event.target as Node)) {
        return;
      }

      closeMenu();
    },
    [baseId, closeMenu, openFilterKey]
  );

  if (!fields || !visibleFilters.length) {
    return <></>;
  }

  const sitesFound = resolveSitesFoundText(fields.sitesFoundText?.value, sitesFoundCount);
  const sitesCountColor = theme.palette.neutral11?.main;

  const getAppliedCount = (facetKey: LocationSearchFacetKey): number => {
    void facetTick;
    return getSelectedFacetValues(getFacetController(facetControllers, facetKey)).length;
  };

  const hasActiveFacetFilters: boolean =
    getAppliedCount('condition') > 0 ||
    getAppliedCount('status') > 0 ||
    getAppliedCount('ageGroup') > 0 ||
    getAppliedCount('language') > 0;
  const canClearFilters =
    hasCommunityFilter || Boolean(selectedDistanceLabel) || hasActiveFacetFilters;

  const openFilterItem = visibleFilters.find((item) => resolveFilterKey(item) === openFilterKey);
  const openMenuId = openFilterKey ? `${baseId}-${openFilterKey}-menu` : undefined;

  void facetTick;
  const openMenuOptionCount =
    openFilterKey === LOCATION_SEARCH_FILTER_KEYS.DISTANCE
      ? distanceOptions.length
      : openFilterKey
        ? (getFacetController(facetControllers, openFilterKey as LocationSearchFacetKey)?.state
            .values.length ?? 0)
        : 0;
  const isMenuScrollable = openMenuOptionCount > MAX_VISIBLE_FILTER_OPTIONS;
  const isMultiSelectMenu = Boolean(
    openFilterKey && openFilterKey !== LOCATION_SEARCH_FILTER_KEYS.DISTANCE
  );
  const isMenuOpen = Boolean(openFilterKey && anchorEl);

  return (
    <StFilterBar id="location-search-filter-bar">
      <StFilterBarInner>
        <StFilterPills>
          {visibleFilters.map((item) => {
            const label = resolveFilterLabel(item);
            const filterKey = resolveFilterKey(item);
            if (!label || !filterKey) {
              return null;
            }

            const isDistance = filterKey === LOCATION_SEARCH_FILTER_KEYS.DISTANCE;
            const isDisabled = isDistance && isDistanceDisabled;
            const isOpen = openFilterKey === filterKey;
            const isActive = isDistance
              ? Boolean(selectedDistanceLabel) && !isDistanceDisabled
              : getAppliedCount(filterKey as LocationSearchFacetKey) > 0;

            return (
              <FilterPill
                key={item.id || filterKey}
                item={item}
                filterKey={filterKey}
                label={label}
                isOpen={isOpen}
                isDisabled={isDisabled}
                isActive={isActive}
                pillId={`${baseId}-${filterKey}-pill`}
                openMenuId={openMenuId}
                appliedCount={getAppliedCount(filterKey as LocationSearchFacetKey)}
                selectedDistanceLabel={selectedDistanceLabel}
                isDistanceDisabled={isDistanceDisabled}
                onOpen={openMenu}
              />
            );
          })}
        </StFilterPills>

        <StFilterMeta id="location-search-filter-bar-meta">
          <UiText
            size="body2"
            styles={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              color: sitesCountColor,
            }}
          >
            {sitesFound}
          </UiText>

          {Boolean(fields.clearFilterLabel?.value) && (
            <StClearFilterButtonWrap>
              <ButtonDiscreet
                type="button"
                disabled={!canClearFilters}
                aria-disabled={!canClearFilters}
                onClick={handleClearAll}
              >
                <Text field={fields.clearFilterLabel} />
              </ButtonDiscreet>
            </StClearFilterButtonWrap>
          )}
        </StFilterMeta>
      </StFilterBarInner>

      <Popper
        open={isMenuOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        modifiers={POPPER_MODIFIERS}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            timeout={GROW_TIMEOUT}
            onEntered={() => focusFirstMenuItem(menuPaperRef.current)}
          >
            <Box sx={{ transformOrigin: 'top left' }}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <StFilterMenuPaper
                  ref={menuPaperRef}
                  $withFooter={isMultiSelectMenu}
                  $scrollable={isMenuScrollable}
                  onKeyDown={handleMenuKeyDown}
                >
                  {openFilterKey === LOCATION_SEARCH_FILTER_KEYS.DISTANCE && (
                    <DistanceFilterMenu
                      options={distanceOptions}
                      selectedLabel={selectedDistanceLabel}
                      menuId={openMenuId || `${baseId}-distance-menu`}
                      onSelect={handleDistanceSelect}
                    />
                  )}

                  {openFilterKey && isMultiSelectMenu && openFilterItem && (
                    <FacetFilterMenuPanel
                      facetKey={openFilterKey as LocationSearchFacetKey}
                      facetControllers={facetControllers}
                      openFilterItem={openFilterItem}
                      openMenuId={openMenuId || `${baseId}-${openFilterKey}-menu`}
                      stagedSelections={stagedSelections}
                      onStagedChange={handleStagedChange}
                      onClear={handleFacetClear}
                      onApply={handleFacetApply}
                    />
                  )}
                </StFilterMenuPaper>
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
    </StFilterBar>
  );
}

export default LocationSearchFilterBar;
